import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  ripple: boolean;
  menuMode: string;
  scale: number;
}

interface LayoutState {
  profileSidebarVisible: boolean;
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  staticMenuMobileActive: boolean;
  showHealthIndicator: boolean;
  selectedFactory: string;
  selectedSector: string;
  selectedSubSector: string;
  selectedPlant: string;
  selectedPlannerGroups: string[];
  selectedWorkCenters: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  _config: AppConfig = {
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'lara-light-indigo',
    scale: 14,
  };

  config = signal<AppConfig>(this._config);

  state: LayoutState = {
    profileSidebarVisible: false,
    staticMenuDesktopInactive: true,
    overlayMenuActive: false,
    staticMenuMobileActive: false,
    showHealthIndicator: false,
    selectedFactory: 'all',
    selectedSector: 'all',
    selectedSubSector: 'all',
    selectedPlant: 'all',
    selectedPlannerGroups: [],
    selectedWorkCenters: [],
  };

  private readonly HEALTH_INDICATOR_KEY = 'showHealthIndicator';
  private readonly SELECTED_FACTORY_KEY = 'selectedFactory';
  private readonly SELECTED_SECTOR_KEY = 'selectedSector';
  private readonly SELECTED_SUBSECTOR_KEY = 'selectedSubSector';
  private readonly SELECTED_PLANT_KEY = 'selectedPlant';
  private readonly SELECTED_PLANNER_GROUPS_KEY = 'selectedPlannerGroups';
  private readonly SELECTED_WORK_CENTERS_KEY = 'selectedWorkCenters';

  private overlayOpen = new Subject<any>();
  overlayOpen$ = this.overlayOpen.asObservable();

  showProfileSidebar() {
    this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
    if (this.state.profileSidebarVisible) {
      this.overlayOpen.next(null);
    }
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;
      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive =
        !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;
      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  getShowHealthIndicator(): boolean {
    const saved = localStorage.getItem(this.HEALTH_INDICATOR_KEY);
    return saved === 'true';
  }

  setShowHealthIndicator(value: boolean): void {
    this.state.showHealthIndicator = value;
    localStorage.setItem(this.HEALTH_INDICATOR_KEY, String(value));
  }

  getSelectedFactory(): string {
    const saved = localStorage.getItem(this.SELECTED_FACTORY_KEY);
    return saved || 'all';
  }

  setSelectedFactory(value: string): void {
    this.state.selectedFactory = value;
    localStorage.setItem(this.SELECTED_FACTORY_KEY, value);
  }

  getSelectedSector(): string {
    const saved = localStorage.getItem(this.SELECTED_SECTOR_KEY);
    return saved || 'all';
  }

  setSelectedSector(value: string): void {
    this.state.selectedSector = value;
    localStorage.setItem(this.SELECTED_SECTOR_KEY, value);
  }

  getSelectedSubSector(): string {
    const saved = localStorage.getItem(this.SELECTED_SUBSECTOR_KEY);
    return saved || 'all';
  }

  setSelectedSubSector(value: string): void {
    this.state.selectedSubSector = value;
    localStorage.setItem(this.SELECTED_SUBSECTOR_KEY, value);
  }

  getSelectedPlant(): string {
    const saved = localStorage.getItem(this.SELECTED_PLANT_KEY);
    return saved || 'all';
  }

  setSelectedPlant(value: string): void {
    this.state.selectedPlant = value;
    localStorage.setItem(this.SELECTED_PLANT_KEY, value);
  }

  getSelectedPlannerGroups(): string[] {
    const saved = localStorage.getItem(this.SELECTED_PLANNER_GROUPS_KEY);
    if (!saved) return [];
    try {
      return JSON.parse(saved);
    } catch {
      return [];
    }
  }

  setSelectedPlannerGroups(value: string[]): void {
    this.state.selectedPlannerGroups = value;
    localStorage.setItem(
      this.SELECTED_PLANNER_GROUPS_KEY,
      JSON.stringify(value),
    );
  }

  getSelectedWorkCenters(): string[] {
    const saved = localStorage.getItem(this.SELECTED_WORK_CENTERS_KEY);
    if (!saved) return [];
    try {
      return JSON.parse(saved);
    } catch {
      return [];
    }
  }

  setSelectedWorkCenters(value: string[]): void {
    this.state.selectedWorkCenters = value;
    localStorage.setItem(this.SELECTED_WORK_CENTERS_KEY, JSON.stringify(value));
  }

  constructor() {
    this.state.showHealthIndicator = this.getShowHealthIndicator();
    this.state.selectedFactory = this.getSelectedFactory();
    this.state.selectedSector = this.getSelectedSector();
    this.state.selectedSubSector = this.getSelectedSubSector();
    this.state.selectedPlant = this.getSelectedPlant();
    this.state.selectedPlannerGroups = this.getSelectedPlannerGroups();
    this.state.selectedWorkCenters = this.getSelectedWorkCenters();
  }
}
